import {
  Add as AddIcon,
  Business as BusinessIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { ptBR } from "date-fns/locale";
import { useCallback, useEffect, useState } from "react";
import representadoService from "../services/representado";
import { ESTADOS_BRASIL } from "../utils/constants";
import {
  formatCEP,
  formatCpfCnpj,
  formatNumero,
  formatPhone,
} from "../utils/formatters";
import {
  isRepresentadoPessoaJuridica,
  obterNomeExibicaoRepresentado,
  TIPO_REPRESENTADO_PESSOA_FISICA,
  TIPO_REPRESENTADO_PESSOA_JURIDICA,
} from "../utils/obterNomeExibicaoUsuario";
import {
  criarFormularioRepresentadoVazio,
  montarFormularioDeRepresentado,
  montarPayloadRepresentado,
  REPRESENTADO_CHAR_LIMITS,
  validarFormularioRepresentado,
} from "../utils/representadoForm";
import CharCounter from "./CharCounter";

const CEP_DIGITS = 8;

function extrairMensagensDeErro(error) {
  if (error?.errors) {
    return Object.values(error.errors).flat();
  }
  if (error?.message) {
    return [error.message];
  }
  return ["Erro desconhecido."];
}

function CampoTexto({
  label,
  name,
  value,
  onChange,
  maxLength,
  required = true,
  type,
  disabled,
}) {
  return (
    <TextField
      required={required}
      fullWidth
      label={label}
      name={name}
      type={type}
      disabled={disabled}
      value={value ?? ""}
      onChange={onChange}
      inputProps={{ maxLength }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <CharCounter value={value} maxLength={maxLength} />
          </InputAdornment>
        ),
      }}
    />
  );
}

function RepresentadoCard({ representado, onEditar, onExcluir }) {
  const isPessoaJuridica = isRepresentadoPessoaJuridica(representado);
  const nomeExibicao = obterNomeExibicaoRepresentado(representado, "Sem nome");
  const localizacao = [
    representado.endereco?.municipio,
    representado.endereco?.estado,
  ]
    .filter(Boolean)
    .join(" - ");

  return (
    <Paper
      variant="outlined"
      data-testid="representado-card"
      sx={{ p: 2, borderRadius: 2, height: "100%" }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={1}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={600} noWrap>
            {nomeExibicao}
          </Typography>
          <Chip
            size="small"
            icon={isPessoaJuridica ? <BusinessIcon /> : <PersonIcon />}
            label={isPessoaJuridica ? "Pessoa Jurídica" : "Pessoa Física"}
            sx={{ mt: 0.5, mb: 1 }}
          />
        </Box>
        <Stack direction="row">
          <Tooltip title="Editar representado">
            <IconButton
              size="small"
              aria-label={`Editar ${nomeExibicao}`}
              onClick={() => onEditar(representado)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir representado">
            <IconButton
              size="small"
              color="error"
              aria-label={`Excluir ${nomeExibicao}`}
              onClick={() => onExcluir(representado)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      <Typography variant="body2" color="text.secondary">
        {isPessoaJuridica ? "CNPJ" : "CPF"}:{" "}
        {representado.cpf ? formatCpfCnpj(String(representado.cpf)) : "—"}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Email: {representado.email || "—"}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Telefone:{" "}
        {representado.telefone ? formatPhone(representado.telefone) : "—"}
      </Typography>
      {localizacao && (
        <Typography variant="body2" color="text.secondary">
          {localizacao}
        </Typography>
      )}
    </Paper>
  );
}

function RepresentadosManager() {
  const [representados, setRepresentados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erroListagem, setErroListagem] = useState("");
  const [dialogAberto, setDialogAberto] = useState(false);
  const [representadoEmEdicaoId, setRepresentadoEmEdicaoId] = useState(null);
  const [formulario, setFormulario] = useState(criarFormularioRepresentadoVazio());
  const [semNumero, setSemNumero] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [errosModal, setErrosModal] = useState([]);
  const [representadoParaExcluir, setRepresentadoParaExcluir] = useState(null);
  const [excluindo, setExcluindo] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const isPessoaJuridica =
    formulario.tipo === TIPO_REPRESENTADO_PESSOA_JURIDICA;

  const carregarRepresentados = useCallback(async () => {
    setLoading(true);
    setErroListagem("");
    try {
      const itens = await representadoService.listar();
      setRepresentados(itens);
    } catch (error) {
      setErroListagem(extrairMensagensDeErro(error).join(" "));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarRepresentados();
  }, [carregarRepresentados]);

  const abrirDialogNovo = () => {
    setRepresentadoEmEdicaoId(null);
    setFormulario(criarFormularioRepresentadoVazio());
    setSemNumero(false);
    setErrosModal([]);
    setDialogAberto(true);
  };

  const abrirDialogEdicao = (representado) => {
    const formularioExistente = montarFormularioDeRepresentado(representado);
    setRepresentadoEmEdicaoId(representado.id);
    setFormulario(formularioExistente);
    setSemNumero(!formularioExistente.numero);
    setErrosModal([]);
    setDialogAberto(true);
  };

  const fecharDialog = () => {
    if (salvando) return;
    setDialogAberto(false);
    setErrosModal([]);
  };

  const handleTipoChange = (_, novoTipo) => {
    if (novoTipo === null || representadoEmEdicaoId) return;
    setFormulario((anterior) => ({ ...anterior, tipo: novoTipo }));
  };

  const buscarCep = async (cepFormatado) => {
    const digitos = cepFormatado.replace(/\D/g, "");
    if (digitos.length !== CEP_DIGITS) return;
    setCepLoading(true);
    try {
      const resposta = await fetch(`https://viacep.com.br/ws/${digitos}/json/`);
      const dados = await resposta.json();
      if (!dados.erro) {
        setFormulario((anterior) => ({
          ...anterior,
          rua: dados.logradouro || anterior.rua,
          municipio: dados.localidade || anterior.municipio,
          estado: dados.uf || anterior.estado,
        }));
      }
    } catch {
    } finally {
      setCepLoading(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    let valorFormatado = value;

    switch (name) {
      case "documento":
        valorFormatado = formatCpfCnpj(value);
        break;
      case "telefone":
        valorFormatado = formatPhone(value);
        break;
      case "cep":
        valorFormatado = formatCEP(value);
        buscarCep(valorFormatado);
        break;
      case "numero":
        if (semNumero) return;
        valorFormatado = formatNumero(value);
        break;
      default:
        break;
    }

    setFormulario((anterior) => ({ ...anterior, [name]: valorFormatado }));
  };

  const handleSemNumeroChange = (event) => {
    const marcado = event.target.checked;
    setSemNumero(marcado);
    setFormulario((anterior) => ({
      ...anterior,
      numero: marcado ? "" : anterior.numero,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrosModal([]);

    const errosDeValidacao = validarFormularioRepresentado(
      formulario,
      semNumero,
    );
    if (errosDeValidacao.length > 0) {
      setErrosModal(errosDeValidacao);
      return;
    }

    setSalvando(true);
    try {
      const payload = montarPayloadRepresentado(formulario, semNumero);
      if (representadoEmEdicaoId) {
        await representadoService.atualizar(representadoEmEdicaoId, payload);
      } else {
        await representadoService.criar(payload);
      }
      setDialogAberto(false);
      setSnackbar({
        open: true,
        message: representadoEmEdicaoId
          ? "Representado atualizado com sucesso!"
          : "Representado cadastrado com sucesso!",
        severity: "success",
      });
      await carregarRepresentados();
    } catch (error) {
      setErrosModal(extrairMensagensDeErro(error));
    } finally {
      setSalvando(false);
    }
  };

  const handleConfirmarExclusao = async () => {
    if (!representadoParaExcluir) return;
    setExcluindo(true);
    try {
      await representadoService.excluir(representadoParaExcluir.id);
      setRepresentadoParaExcluir(null);
      setSnackbar({
        open: true,
        message: "Representado removido com sucesso!",
        severity: "success",
      });
      await carregarRepresentados();
    } catch (error) {
      setRepresentadoParaExcluir(null);
      setSnackbar({
        open: true,
        message: extrairMensagensDeErro(error).join(" "),
        severity: "error",
      });
    } finally {
      setExcluindo(false);
    }
  };

  const fecharSnackbar = () =>
    setSnackbar((anterior) => ({ ...anterior, open: false }));

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        sx={{ mb: 3 }}
      >
        <Typography variant="h6">Representados</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={abrirDialogNovo}
          sx={{ width: { xs: "100%", sm: "auto" } }}
        >
          Novo Representado
        </Button>
      </Stack>

      {erroListagem && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {erroListagem}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : representados.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nenhum representado cadastrado.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {representados.map((representado) => (
            <Grid item xs={12} md={6} key={representado.id}>
              <RepresentadoCard
                representado={representado}
                onEditar={abrirDialogEdicao}
                onExcluir={setRepresentadoParaExcluir}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog
        open={dialogAberto}
        onClose={fecharDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {representadoEmEdicaoId ? "Editar Representado" : "Novo Representado"}
        </DialogTitle>
        <form onSubmit={handleSubmit} aria-label="Formulário de representado">
          <DialogContent>
            {errosModal.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {errosModal.map((mensagem, indice) => (
                    <li key={`${indice}-${mensagem}`}>{mensagem}</li>
                  ))}
                </Box>
              </Alert>
            )}

            <ToggleButtonGroup
              value={formulario.tipo}
              exclusive
              onChange={handleTipoChange}
              aria-label="tipo de representado"
              color="primary"
              fullWidth
              sx={{ mb: 3 }}
            >
              <ToggleButton
                value={TIPO_REPRESENTADO_PESSOA_FISICA}
                aria-label="pessoa física"
                disabled={Boolean(representadoEmEdicaoId)}
              >
                <PersonIcon sx={{ mr: 1 }} />
                Pessoa Física
              </ToggleButton>
              <ToggleButton
                value={TIPO_REPRESENTADO_PESSOA_JURIDICA}
                aria-label="pessoa jurídica"
                disabled={Boolean(representadoEmEdicaoId)}
              >
                <BusinessIcon sx={{ mr: 1 }} />
                Pessoa Jurídica
              </ToggleButton>
            </ToggleButtonGroup>

            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              adapterLocale={ptBR}
            >
              <Grid container spacing={2}>
                {isPessoaJuridica ? (
                  <>
                    <Grid item xs={12} sm={6}>
                      <CampoTexto
                        label="Razão Social"
                        name="razaoSocial"
                        value={formulario.razaoSocial}
                        onChange={handleChange}
                        maxLength={REPRESENTADO_CHAR_LIMITS.razaoSocial}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CampoTexto
                        label="Nome Fantasia"
                        name="nomeFantasia"
                        value={formulario.nomeFantasia}
                        onChange={handleChange}
                        maxLength={REPRESENTADO_CHAR_LIMITS.nomeFantasia}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CampoTexto
                        label="CNPJ"
                        name="documento"
                        value={formulario.documento}
                        onChange={handleChange}
                        maxLength={REPRESENTADO_CHAR_LIMITS.cnpj}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DatePicker
                        label="Data de Constituição"
                        value={formulario.dataConstituicao || null}
                        onChange={(novaData) =>
                          setFormulario((anterior) => ({
                            ...anterior,
                            dataConstituicao: novaData,
                          }))
                        }
                        format="dd/MM/yyyy"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            required: true,
                            name: "dataConstituicao",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <CampoTexto
                        label="Representante Legal"
                        name="representanteLegal"
                        value={formulario.representanteLegal}
                        onChange={handleChange}
                        maxLength={REPRESENTADO_CHAR_LIMITS.representanteLegal}
                      />
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={12} sm={6}>
                      <CampoTexto
                        label="Nome"
                        name="nome"
                        value={formulario.nome}
                        onChange={handleChange}
                        maxLength={REPRESENTADO_CHAR_LIMITS.nome}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CampoTexto
                        label="Sobrenome"
                        name="sobrenome"
                        value={formulario.sobrenome}
                        onChange={handleChange}
                        maxLength={REPRESENTADO_CHAR_LIMITS.sobrenome}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CampoTexto
                        label="CPF"
                        name="documento"
                        value={formulario.documento}
                        onChange={handleChange}
                        maxLength={REPRESENTADO_CHAR_LIMITS.cpf}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <DatePicker
                        label="Data de Nascimento"
                        value={formulario.dataNascimento || null}
                        onChange={(novaData) =>
                          setFormulario((anterior) => ({
                            ...anterior,
                            dataNascimento: novaData,
                          }))
                        }
                        format="dd/MM/yyyy"
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            required: true,
                            name: "dataNascimento",
                          },
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <CampoTexto
                        label="Profissão"
                        name="profissao"
                        value={formulario.profissao}
                        onChange={handleChange}
                        maxLength={REPRESENTADO_CHAR_LIMITS.profissao}
                        required={false}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <CampoTexto
                        label="Empresa"
                        name="empresa"
                        value={formulario.empresa}
                        onChange={handleChange}
                        maxLength={REPRESENTADO_CHAR_LIMITS.empresa}
                        required={false}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <CampoTexto
                        label="Cargo"
                        name="cargo"
                        value={formulario.cargo}
                        onChange={handleChange}
                        maxLength={REPRESENTADO_CHAR_LIMITS.cargo}
                        required={false}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12} sm={6}>
                  <CampoTexto
                    label="Email"
                    name="email"
                    type="email"
                    value={formulario.email}
                    onChange={handleChange}
                    maxLength={REPRESENTADO_CHAR_LIMITS.email}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CampoTexto
                    label="Telefone"
                    name="telefone"
                    value={formulario.telefone}
                    onChange={handleChange}
                    maxLength={REPRESENTADO_CHAR_LIMITS.telefone}
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={600}>
                    Endereço
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={semNumero}
                        onChange={handleSemNumeroChange}
                      />
                    }
                    label="Sem número"
                  />
                </Grid>
                <Grid item xs={12} sm={9}>
                  <CampoTexto
                    label="Rua"
                    name="rua"
                    value={formulario.rua}
                    onChange={handleChange}
                    maxLength={REPRESENTADO_CHAR_LIMITS.rua}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <CampoTexto
                    label="Número"
                    name="numero"
                    value={formulario.numero}
                    onChange={handleChange}
                    maxLength={REPRESENTADO_CHAR_LIMITS.numero}
                    disabled={semNumero}
                    required={!semNumero}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <CampoTexto
                    label="Cidade"
                    name="municipio"
                    value={formulario.municipio}
                    onChange={handleChange}
                    maxLength={REPRESENTADO_CHAR_LIMITS.municipio}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth required>
                    <InputLabel id="representado-estado-label">Estado</InputLabel>
                    <Select
                      labelId="representado-estado-label"
                      name="estado"
                      value={formulario.estado}
                      onChange={handleChange}
                      label="Estado"
                    >
                      <MenuItem value="">
                        <em>Selecione</em>
                      </MenuItem>
                      {ESTADOS_BRASIL.map((estado) => (
                        <MenuItem key={estado.sigla} value={estado.sigla}>
                          {estado.sigla}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    required
                    fullWidth
                    label="CEP"
                    name="cep"
                    value={formulario.cep}
                    onChange={handleChange}
                    inputProps={{ maxLength: REPRESENTADO_CHAR_LIMITS.cep }}
                    InputProps={{
                      endAdornment: cepLoading ? (
                        <InputAdornment position="end">
                          <CircularProgress size={18} />
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                </Grid>
              </Grid>
            </LocalizationProvider>
          </DialogContent>
          <DialogActions>
            <Button onClick={fecharDialog} disabled={salvando}>
              Cancelar
            </Button>
            <Button type="submit" variant="contained" disabled={salvando}>
              {salvando ? "Salvando..." : "Salvar"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={Boolean(representadoParaExcluir)}
        onClose={() => !excluindo && setRepresentadoParaExcluir(null)}
      >
        <DialogTitle>Excluir representado</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Deseja realmente excluir{" "}
            <strong>
              {obterNomeExibicaoRepresentado(representadoParaExcluir)}
            </strong>
            ? Solicitações já enviadas não serão afetadas.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRepresentadoParaExcluir(null)}
            disabled={excluindo}
          >
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmarExclusao}
            disabled={excluindo}
          >
            {excluindo ? "Excluindo..." : "Excluir"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={fecharSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={fecharSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default RepresentadosManager;
